import { fetchWithAuth } from "./http";

/**
 * Hourly time-series exports.
 *
 * Exports are asynchronous by design: a single generator-year is ~8,760 rows
 * and a seven-year pull with every metric selected runs to hundreds of
 * thousands, which is well past what a request/response cycle should carry.
 * So the flow is create -> poll -> download a pre-signed file.
 *
 * The same endpoints back the partner integration, so anything the UI can
 * produce here is reproducible over the API.
 */

// --- Types ---

export interface RegistryMetric {
    key: string;
    label: string;
    unit: string;
    /** 'endpoint' = aggregated damage; 'midpoint' = problem-oriented indicator. */
    family: "endpoint" | "midpoint";
    /** aggregate | air | soil | water | resource */
    compartment: string;
}

export interface RegistryReadiness {
    ba_code: string;
    readiness_through: string | null;
    grid_final_through: string | null;
    model_valid_through: string | null;
    coverage_through: string | null;
    /** Which of the three signals is limiting the frontier. */
    binding_constraint: string | null;
    finality_lag_days: number | null;
}

export interface RegistrySegment {
    model_run_id: string;
    hour_start: string;
    hour_end: string;
    model_version: string | null;
    eco_version: string | null;
}

export interface RegistryVintages {
    project_id: string;
    ba_code: string | null;
    released_through: string | null;
    readiness: RegistryReadiness | null;
    segments: RegistrySegment[];
    sign_convention: string;
    timezone: string;
}

export interface RegistryExportFile {
    project_id: string;
    download_url: string | null;
    rows: number;
    bytes: number;
    s3_key: string | null;
}

export interface RegistryExport {
    export_id: string;
    status: "queued" | "running" | "completed" | "failed";
    created_at: string | null;
    completed_at: string | null;
    project_ids: string[];
    granularity: string;
    format: string;
    metrics: string[] | null;
    lens: string | null;
    files: RegistryExportFile[];
    manifest: Record<string, unknown> | null;
    row_count: number | null;
    error_message: string | null;
    expires_at: string | null;
}

export interface CreateExportRequest {
    project_ids: string[];
    start?: string;
    end?: string;
    granularity?: "hourly" | "daily";
    format?: "csv" | "parquet";
    /** Omit for every metric; 'endpoints' / 'midpoints' / explicit keys. */
    metrics?: string[];
    /**
     * Only 'operational' is available: avoided grid operation, excluding the
     * project's own construction and end-of-life. Defaults to it when omitted.
     */
    lens?: "operational";
    include_provisional?: boolean;
}

// --- Calls ---

export async function listRegistryMetrics(token: string): Promise<RegistryMetric[]> {
    return fetchWithAuth<RegistryMetric[]>("/registry/metrics", token);
}

export async function getVintages(
    token: string,
    projectId: string,
): Promise<RegistryVintages> {
    return fetchWithAuth<RegistryVintages>(
        `/registry/generators/${projectId}/vintages`,
        token,
    );
}

export async function createExport(
    token: string,
    body: CreateExportRequest,
): Promise<RegistryExport> {
    return fetchWithAuth<RegistryExport>("/registry/exports", token, {
        method: "POST",
        body: JSON.stringify(body),
    });
}

export async function getExport(
    token: string,
    exportId: string,
): Promise<RegistryExport> {
    return fetchWithAuth<RegistryExport>(
        `/registry/exports/${exportId}`,
        token,
    );
}

/**
 * Create an export and resolve when it finishes.
 *
 * Polls at a fixed interval rather than backing off: exports typically settle
 * in seconds, and a backoff would add latency to the common case for no gain.
 * `onProgress` reports each status change so the caller can keep the user
 * informed instead of showing an unexplained spinner.
 */
export async function runExportToCompletion(
    token: string,
    body: CreateExportRequest,
    opts?: {
        pollMs?: number;
        timeoutMs?: number;
        onProgress?: (status: RegistryExport["status"]) => void;
    },
): Promise<RegistryExport> {
    const pollMs = opts?.pollMs ?? 3000;
    const timeoutMs = opts?.timeoutMs ?? 10 * 60 * 1000;

    const created = await createExport(token, body);
    opts?.onProgress?.(created.status);

    const deadline = Date.now() + timeoutMs;
    let last = created.status;

    for (;;) {
        if (Date.now() > deadline) {
            throw new Error(
                "Export is taking longer than expected. It is still running — " +
                    "check back shortly rather than starting another.",
            );
        }
        await new Promise((r) => setTimeout(r, pollMs));

        const current = await getExport(token, created.export_id);
        if (current.status !== last) {
            last = current.status;
            opts?.onProgress?.(current.status);
        }
        if (current.status === "completed") return current;
        if (current.status === "failed") {
            throw new Error(current.error_message || "Export failed.");
        }
    }
}

export interface RegistryExportRows {
    export_id: string;
    project_id: string;
    rows_returned: number;
    rows_total: number;
    offset: number;
    truncated: boolean;
    rows: Record<string, string>[];
}

/**
 * Read a completed export's rows as JSON.
 *
 * The download URL is the delivery path; this is the read path. The results
 * bucket has no CORS policy, so a browser cannot fetch a presigned URL, and the
 * API reads the object instead. It returns the bytes the export already
 * produced rather than recomputing, so the explorer cannot drift from the file
 * a partner reconciles against.
 */
export async function getExportRows(
    token: string,
    exportId: string,
    opts?: { projectId?: string; limit?: number; offset?: number },
): Promise<RegistryExportRows> {
    const q = new URLSearchParams();
    if (opts?.projectId) q.set("project_id", opts.projectId);
    if (opts?.limit != null) q.set("limit", String(opts.limit));
    if (opts?.offset != null) q.set("offset", String(opts.offset));
    const qs = q.toString();
    return fetchWithAuth<RegistryExportRows>(
        `/registry/exports/${exportId}/rows${qs ? `?${qs}` : ""}`,
        token,
    );
}
